import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Stack,
  Popover,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  List as ListIcon,
} from "@mui/icons-material";
import {
  GenericAdd,
  GoodIcon,
  BadIcon,
  BOMIcon,
  BOMAddIcon,
  VendorIcon,
  VendorAddIcon,
  POIcon,
  POAddIcon,
  SOIcon,
  SOAddIcon,
  EmployeeIcon,
  EmployeeAddIcon,
  WorkOrderIcon,
  WorkOrderAddIcon,
  CustomerIcon,
  CustomerAddIcon,
  UOMIcon,
  InvMovementIcon,
  InvBalanceIcon,
  ItemIcon,
  LotIcon,
  ShippingIcon,
  InvStatusIcon,
  LocationIcon,
  LocationAddIcon,
  UserIcon,
  ComplaintIcon,
} from "../common/NucleusSymbolsAndIcons";
import { useNavigate, useLocation, Link, Outlet } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import stringUtils from "../../utils/stringUtils";
import { isDev } from "../../config/environment";
import { ErrorDisplay } from "../../contexts/ErrorContextManager";
import { useConfirm } from "../../contexts/ConfirmDialogProvider";

const drawerWidth = 240;

interface AppLayoutProps {
  children?: React.ReactNode;
}

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  role: string | undefined;
  options: {
    name: string;
    role: string | undefined;
    path: string;
    icon: React.ReactElement;
  }[];
}

const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
const thisTime = new Date("Tue, 12 Aug 2025 20:15:48 GMT");
// console.log("THIS TIME", thisTime, new Date());
const thisTimeString = thisTime.toDateString();
const thisTimeCentralString = thisTime.toLocaleDateString("en-us", {
  timeZone: "America/Chicago",
});
const thisTimeUTCString = thisTime.toUTCString();

const navItems: NavigationItem[] = [
  {
    text: "BOMs",
    icon: <BOMIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <BOMAddIcon />,
        path: "/manufacturing/boms/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/manufacturing/boms",
      },
    ],
  },
  {
    text: "Customers",
    icon: <CustomerIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <CustomerAddIcon />,
        path: "/companies/form?mode=create&type=customer",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/companies?type=customer",
      },
      {
        name: "New Customer Complaint",
        role: undefined,
        icon: <ComplaintIcon />,
        path: "/quality/customer_complaint/form?mode=create",
      },
      {
        name: "Customer Complaint List",
        role: undefined,
        icon: <ListIcon />,
        path: "/quality/customer_complaint",
      },
    ],
  },
  {
    text: "Employees",
    icon: <EmployeeIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <EmployeeAddIcon />,
        path: "/people/form?mode=create&type=employee",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/people?type=employee",
      },
    ],
  },
  {
    text: "Inventory Status",
    icon: <InvStatusIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <GenericAdd />,
        path: "/inventory/status/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/inventory/status",
      },
    ],
  },
  {
    text: "Items",
    icon: <ItemIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <GenericAdd />,
        path: "/inventory/items/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/inventory/items",
      },
    ],
  },
  {
    text: "Locations",
    icon: <LocationIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <LocationAddIcon />,
        path: "/locations/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/locations",
      },
    ],
  },
  {
    text: "Lot Balances",
    icon: <InvBalanceIcon />,
    role: undefined,
    options: [
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/inventory/balances",
      },
    ],
  },
  {
    text: "Lots",
    icon: <LotIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <GenericAdd />,
        path: "/inventory/lots/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/inventory/lots",
      },
    ],
  },
  {
    text: "Movements",
    icon: <InvMovementIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <GenericAdd />,
        path: "/inventory/movements/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/inventory/movements",
      },
    ],
  },
  {
    text: "Purchase Orders",
    icon: <POIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <POAddIcon />,
        path: "/orders/purchase/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/orders/purchase",
      },
      {
        name: "Line List",
        role: undefined,
        icon: <ListIcon />,
        path: "/orders/po-lines",
      },
    ],
  },
  {
    text: "Sales Orders",
    icon: <SOIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <SOAddIcon />,
        path: "/orders/sales/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/orders/sales",
      },
      {
        name: "Fulfillments",
        role: undefined,
        icon: <ListIcon />,
        path: "/orders/fulfillments",
      },
    ],
  },
  {
    text: "Shipments",
    icon: <ShippingIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <GenericAdd />,
        path: "/shipments/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/shipments",
      },
    ],
  },
  {
    text: "Units of Measure",
    icon: <UOMIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <GenericAdd />,
        path: "/inventory/uoms/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/inventory/uoms",
      },
    ],
  },
  {
    text: "Users",
    icon: <UserIcon />,
    role: "superuser",
    options: [
      {
        name: "Create",
        role: "superuser",
        icon: <GenericAdd />,
        path: "/users/form?mode=create",
      },
      {
        name: "User List",
        role: "superuser",
        icon: <ListIcon />,
        path: "/users",
      },
      {
        name: "Bot List",
        role: "superuser",
        icon: <ListIcon />,
        path: "/bot-tokens",
      },
    ],
  },
  {
    text: "Vendors",
    icon: <VendorIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <VendorAddIcon />,
        path: "/companies/form?mode=create&type=vendor",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/companies?type=vendor",
      },
    ],
  },
  {
    text: "Work Orders",
    icon: <WorkOrderIcon />,
    role: undefined,
    options: [
      {
        name: "Create",
        role: undefined,
        icon: <WorkOrderAddIcon />,
        path: "/manufacturing/orders/form?mode=create",
      },
      {
        name: "List",
        role: undefined,
        icon: <ListIcon />,
        path: "/manufacturing/orders",
      },
    ],
  },
  {
    text: "MRP Utilities",
    icon: <ItemIcon />,
    role: undefined,
    options: [
      {
        name: "Demand Forecasting",
        role: undefined,
        icon: <ItemIcon />,
        path: "/mrp/demand_forecasting",
      },
      {
        name: "Low Stock",
        role: undefined,
        icon: <ItemIcon />,
        path: "/mrp/low_stock",
      },
    ],
  },
  {
    text: "RMA/Servicing",
    icon: <ItemIcon />,
    role: undefined,
    options: [
      {
        name: "RMA List",
        role: undefined,
        icon: <ItemIcon />,
        path: "/quality/returned_material",
      },
    ],
  },
  {
    text: "Quality",
    icon: <ItemIcon />,
    role: undefined,
    options: [
      {
        name: "Issue Tracking",
        role: undefined,
        icon: <ItemIcon />,
        path: "/quality/issue-tracking",
      },
    ],
  },
];

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState<boolean>(!isMobile);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [errorMenuAnchor, setErrorMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [contextMenuPlacement, setContextMenuPlacement] = useState<
    "top" | "bottom"
  >("top");
  const [contextMenuAnchor, setContextMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [contextMenuOptions, setContextMenuOptions] = useState<
    {
      name: string;
      role: string | undefined;
      icon: React.ReactElement;
      path: string;
    }[]
  >([]);
  const [middleMouseTarget, setMiddleMouseTarget] =
    useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();

  const [btnColor, setBtnColor] = useState<"good" | "bad">("good");

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleContextMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    options: {
      name: string;
      role: string | undefined;
      icon: React.ReactElement;
      path: string;
    }[],
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const menuEstimatedHeight = options.length * 48;
    setContextMenuPlacement(
      spaceBelow < menuEstimatedHeight ? "top" : "bottom",
    );
    setContextMenuAnchor(event.currentTarget);
    setContextMenuOptions(options);
  };

  const handleContextMenuClose = () => {
    setContextMenuAnchor(null);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleErrorMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setErrorMenuAnchor(event.currentTarget);
  };

  const handleErrorMenuClose = () => {
    setErrorMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      const ok = await confirm({
        title: "Log Out?",
        message: "You will need to log back in to access the system.",
        confirmText: "Log Out",
      });
      if (!ok) return;
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
    handleUserMenuClose();
  };

  const navigationButtonStyles = {
    "&.Mui-selected": {
      backgroundColor: "rgba(0, 134, 117, 0.08)",
      borderRight: `3px solid ${theme.palette.primary.main}`,
      "& .MuiListItemIcon-root": {
        color: theme.palette.primary.main,
      },
      "& .MuiListItemText-primary": {
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
    },
    "&:hover": {
      backgroundColor: "rgba(0, 134, 117, 0.04)",
    },
  };

  const renderNavCategories = () => (
    <>
      {/* Dashboard - Standalone Item */}
      <ListItem disablePadding>
        <ListItemButton
          selected={
            location.pathname === "/" || location.pathname === "/dashboard"
          }
          onClick={() => handleNavigation("/dashboard")}
          // component="a"
          // href="/dashboard"
          sx={navigationButtonStyles}
        >
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>
      </ListItem>

      {navItems.map((item) => {
        if (item.role !== undefined) {
          switch (item.role) {
            case "superuser":
              if (!user?.is_superuser) {
                return <></>;
              }
              break;

            case "admin":
              if (user?.role !== "admin" && !user?.is_superuser) {
                return <></>;
              }
              break;

            case "manager":
              if (
                !["admin", "manager"].includes(user?.role || "") &&
                !user?.is_superuser
              ) {
                return <></>;
              }
              break;

            case "user":
              if (
                !["admin", "manager", "user"].includes(user?.role || "") &&
                !user?.is_superuser
              ) {
                return <></>;
              }
              break;
          }
        }
        const isSelected = item.options.some((option) => {
          return `${location.pathname}${location.search}` === option.path;
        });
        return (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={isSelected}
                onClick={(e) => handleContextMenuOpen(e, item.options)}
                sx={navigationButtonStyles}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          </React.Fragment>
        );
      })}
    </>
  );

  const drawer = (
    <Box displayPrint="none">
      <Toolbar>
        <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
          ERP System
        </Typography>
      </Toolbar>
      <List>{renderNavCategories()}</List>
    </Box>
  );

  const roleName: string = stringUtils.capFirstLetter(user?.role || "NOROLE");

  return (
    <Box sx={{ display: "flex", displayPrint: "none" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: drawerOpen ? `calc(100% - ${drawerWidth}px)` : "100%" },
          ml: { md: drawerOpen ? `${drawerWidth}px` : 0 },
          transition: "width 0.3s ease, margin 0.3s ease",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={[{ mr: 2 }]}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ fontWeight: 700, flexGrow: 1 }}
          >
            {"Centese Nucleus".concat(
              isDev ? ` !!!DEVELOPMENT ENVIRONMENT!!!` : "",
            )}
          </Typography>

          {/* User Menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* User Info */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {user?.is_superuser && (
                <>
                  <Chip
                    label="View Errors"
                    clickable={false}
                    onClick={handleErrorMenuOpen}
                    color="error"
                    size="small"
                  />
                  <Popover
                    open={Boolean(errorMenuAnchor)}
                    anchorEl={errorMenuAnchor}
                    onClose={handleErrorMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                  >
                    <ErrorDisplay />
                  </Popover>
                </>
              )}
              {user?.is_superuser && (
                <Chip
                  label="SuperUser"
                  clickable={false}
                  onClick={() => {}}
                  color="error"
                  size="small"
                />
              )}
              {roleName && (
                <Chip
                  label={roleName}
                  clickable={false}
                  onClick={() => {}}
                  color="warning"
                  size="small"
                />
              )}
            </Box>

            {/* User Menu Button */}
            <IconButton
              color="inherit"
              onClick={handleUserMenuOpen}
              sx={{ p: 0.5 }}
            >
              <Avatar
                sx={{ width: 32, height: 32, bgcolor: "rgba(255,255,255,0.2)" }}
              >
                <AccountCircleIcon />
              </Avatar>
            </IconButton>

            {/* User Menu */}
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              onClick={handleUserMenuClose}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                    mt: 1.5,
                    "& .MuiAvatar-root": {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    "&:before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={handleUserMenuClose}>
                <Avatar />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {user?.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.is_superuser ? "Administrator" : "User"}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { md: drawerOpen ? drawerWidth : 0 },
          flexShrink: { md: 0 },
          transition: "width 0.3s ease",
        }}
        aria-label="navigation"
      >
        {/* Desktop drawer */}
        <Drawer
          variant="persistent"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Menu
          anchorEl={contextMenuAnchor}
          open={Boolean(contextMenuAnchor)}
          onClose={handleContextMenuClose}
          // onClick={handleContextMenuClose}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 1.5,
                // "& .MuiAvatar-root": {
                //   width: 32,
                //   height: 32,
                //   ml: -0.5,
                //   mr: 1,
                // },
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: contextMenuPlacement === "top" ? undefined : 0,
                  bottom: contextMenuPlacement === "top" ? 0 : undefined,
                  left: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            },
          }}
          anchorOrigin={{
            horizontal: "right",
            vertical: contextMenuPlacement === "top" ? "top" : "bottom",
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: contextMenuPlacement === "top" ? "bottom" : "top",
          }}
        >
          {contextMenuOptions.map((opt) => {
            return (
              <ListItem key={opt.name}>
                <ListItemButton
                  onClick={(event) => {
                    console.log("CLICKED WITH BUTTON", event.button);
                    handleContextMenuClose();
                    navigate(opt.path);
                  }}
                  onAuxClick={(event) => {
                    console.log("AUX CLICKED WITH BUTTON", event.button);
                    handleContextMenuClose();
                    if (event.button === 1) {
                      window.open(opt.path, "_blank");
                    }
                  }}
                  sx={navigationButtonStyles}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>{opt.icon}</ListItemIcon>
                  <ListItemText
                    primary={opt.name}
                    slotProps={{
                      primary: {
                        variant: "body2",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </Menu>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          minHeight: "100vh",
          height: "100vh",
          overflow: "auto",
          backgroundColor: theme.palette.background.default,
          display: "flex",
          flexDirection: "column",
          border: 1,
          displayPrint: "flex",
        }}
      >
        <Toolbar />
        <Box
          sx={{
            // flexGrow: 1,
            mr: "3vw",
            ml: "3vw",
            overflow: "auto",
            paddingBottom: 2,
          }}
        >
          <Outlet />
          {/* {children} */}
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
